import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { User, MapPin, Calendar, Loader, Edit, Trash2, MessageCircle, AlertTriangle } from 'lucide-react';
import { ref, get, remove, update, push } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import { database, auth } from '../../shared/firebase';
import { getProvinceName } from '../utils/provinces';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

export default function AdDetail() {
  const { adId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [ad, setAd] = useState(null);
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactMessage, setContactMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => setUser(currentUser));
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const loadAd = async () => {
      const adSnap = await get(ref(database, `vjb_ads/${adId}`));
      if (adSnap.exists()) {
        const adData = adSnap.val();
        setAd(adData);
        const profileSnap = await get(ref(database, `vjb_profiles/${adData.createdBy}`));
        if (profileSnap.exists()) setCreator(profileSnap.val());
      }
      setLoading(false);
    };
    loadAd();
  }, [adId]);

  const isOwner = user && ad?.createdBy === user.uid;
  const handleDelete = async () => { await remove(ref(database, `vjb_ads/${adId}`)); navigate('/profiel'); };
  const toggleStatus = async () => {
    const newStatus = ad.status === 'active' ? 'closed' : 'active';
    await update(ref(database, `vjb_ads/${adId}`), { status: newStatus });
    setAd({ ...ad, status: newStatus });
  };

  const handleContact = async () => {
    if (!user || !contactMessage.trim()) return;
    setSending(true);
    try {
      const newRef = push(ref(database, 'vjb_contact_requests'));
      await update(newRef, {
        fromUid: user.uid, toUid: ad.createdBy, adId, adTitle: ad.title,
        message: contactMessage.trim(), status: 'pending', createdAt: new Date().toISOString(), lastMessageAt: new Date().toISOString()
      });
      setShowContactModal(false);
      setContactMessage('');
      navigate('/berichten');
    } catch (error) {
      console.error('Contact error:', error);
      alert('Er ging iets mis');
    }
    setSending(false);
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });

  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center"><Loader className="animate-spin text-[#8146FF]" size={32} /></div>;

  if (!ad) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <Navbar />
        <main className="flex-1 flex items-center justify-center"><div className="text-center"><h1 className="text-2xl font-bold mb-4">Oproep niet gevonden</h1><Link to="/zoeken" className="text-[#8146FF] hover:underline">Bekijk alle oproepen</Link></div></main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <Navbar />
      <main className="flex-1 max-w-4xl mx-auto px-4 md:px-6 py-8 w-full">
        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2 space-y-6">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <span className={`text-xs px-3 py-1 rounded-full ${ad.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-white/10 text-white/50'}`}>{ad.status === 'active' ? 'Actief' : 'Gesloten'}</span>
                {isOwner && (
                  <div className="flex gap-2">
                    <Link to={`/oproep/${adId}/bewerken`} className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition"><Edit size={18} /></Link>
                    <button onClick={() => setShowDeleteModal(true)} className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition"><Trash2 size={18} /></button>
                  </div>
                )}
              </div>
              <h1 className="text-2xl md:text-3xl font-bold mb-4">{ad.title}</h1>
              <div className="flex flex-wrap gap-4 text-white/60 text-sm">
                <span className="flex items-center gap-1"><MapPin size={14} />{ad.location?.city}, {getProvinceName(ad.location?.province)}</span>
                <span className="flex items-center gap-1"><Calendar size={14} />{formatDate(ad.createdAt)}</span>
              </div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <h2 className="font-semibold mb-4">Zoekt</h2>
              <div className="flex flex-wrap gap-2">{ad.lookingFor?.map((item, idx) => <span key={idx} className="bg-[#8146FF] text-white px-3 py-1 rounded-full text-sm">{item}</span>)}</div>
            </div>
            {ad.genres?.length > 0 && (
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <h2 className="font-semibold mb-4">Genres</h2>
                <div className="flex flex-wrap gap-2">{ad.genres.map((genre, idx) => <span key={idx} className="bg-white/10 text-white/70 px-3 py-1 rounded-full text-sm">{genre}</span>)}</div>
              </div>
            )}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <h2 className="font-semibold mb-4">Beschrijving</h2>
              <p className="text-white/70 whitespace-pre-wrap">{ad.description}</p>
            </div>
          </div>
          <div className="space-y-6">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <h3 className="font-semibold mb-4">Geplaatst door</h3>
              <Link to={`/muzikant/${ad.createdBy}`} className="flex items-center gap-4 group">
                <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center overflow-hidden">
                  {creator?.photoUrl ? <img src={creator.photoUrl} alt={creator.displayName} className="w-full h-full object-cover" /> : <User size={28} className="text-white/40" />}
                </div>
                <div><p className="font-medium group-hover:text-[#8146FF] transition">{creator?.displayName || 'Onbekend'}</p>{creator?.location && <p className="text-white/50 text-sm">{creator.location.city}</p>}</div>
              </Link>
              {creator?.instruments?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-white/10"><div className="flex flex-wrap gap-1">{creator.instruments.slice(0, 4).map((inst, idx) => <span key={idx} className="bg-white/10 text-white/60 text-xs px-2 py-1 rounded">{inst}</span>)}</div></div>
              )}
            </div>
            {!isOwner && user && ad.status === 'active' && (
              <button onClick={() => setShowContactModal(true)} className="w-full bg-[#8146FF] hover:bg-[#6a35d9] text-white py-3 rounded-full font-semibold transition flex items-center justify-center gap-2"><MessageCircle size={20} /> Stuur bericht</button>
            )}
            {!user && <Link to="/inloggen" className="block w-full bg-[#8146FF] hover:bg-[#6a35d9] text-white py-3 rounded-full font-semibold transition text-center">Log in om te reageren</Link>}
            {isOwner && (
              <button onClick={toggleStatus} className={`w-full py-3 rounded-full font-semibold transition ${ad.status === 'active' ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-green-500/20 hover:bg-green-500/30 text-green-400'}`}>{ad.status === 'active' ? 'Markeer als gesloten' : 'Opnieuw activeren'}</button>
            )}
          </div>
        </div>
      </main>
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 max-w-sm w-full">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center"><AlertTriangle size={24} className="text-red-400" /></div>
            <h3 className="text-xl font-bold text-center mb-2">Oproep verwijderen?</h3>
            <p className="text-white/60 text-center text-sm mb-6">Dit kan niet ongedaan worden.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowDeleteModal(false)} className="flex-1 py-2 rounded-lg border border-white/20 text-white/70 hover:bg-white/5 transition">Annuleren</button>
              <button onClick={handleDelete} className="flex-1 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition">Verwijderen</button>
            </div>
          </div>
        </div>
      )}
      {showContactModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">Stuur een bericht</h3>
            <p className="text-white/60 text-sm mb-4">Naar {creator?.displayName} over "{ad.title}"</p>
            <textarea value={contactMessage} onChange={(e) => setContactMessage(e.target.value)} rows={4} maxLength={500} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#8146FF] resize-none mb-4" placeholder="Stel jezelf voor..." />
            <div className="flex gap-3">
              <button onClick={() => setShowContactModal(false)} className="flex-1 py-2 rounded-lg border border-white/20 text-white/70 hover:bg-white/5 transition" disabled={sending}>Annuleren</button>
              <button onClick={handleContact} disabled={sending || !contactMessage.trim()} className="flex-1 py-2 rounded-lg bg-[#8146FF] text-white hover:bg-[#6a35d9] transition disabled:opacity-50 flex items-center justify-center gap-2">{sending ? <Loader className="animate-spin" size={16} /> : <MessageCircle size={16} />} {sending ? 'Verzenden...' : 'Verzenden'}</button>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
}
